import React, { useState, useEffect, useRef, useCallback } from 'react';
import Loader from '../Loader/Loader';
import { formatFileSize } from '../../utils/helper';
import InfiniteScroll from 'react-infinite-scroll-component';
import './CsvViewer.scss';
import Spreadsheet from 'react-spreadsheet';
import { CordovaFileDetail, ElectronFileDetail } from '../../utils/interface';
import { isPlatform } from '@ionic/react';
import { Tooltip } from '@mui/material';

// Web worker setup
const fileWorker = new Worker(new URL("../../workers/fileWorker.js", import.meta.url));

interface Material {
  filename: string;
  filepath: string;
  filesize: number;
  isclassroom: boolean;
  coverimagepath?: string;
  authorname?: string;
  filetype?: string;
}

interface PropsType {
  fileUrl: string;
  fileData: Material | undefined | CordovaFileDetail | ElectronFileDetail | null;
  onClose: () => void;
  type ?: string 
}

const ExcelViewer: React.FC<PropsType> = ({ fileUrl, fileData, onClose ,type}) => {
  const [data, setData] = useState<any[][]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [visibleRows, setVisibleRows] = useState<any[][]>([]);
  const [rowLimit] = useState<number>(100);
  const [hasMore, setHasMore] = useState<boolean>(true);

  useEffect(() => {
    if (fileUrl) {
      setIsLoading(true);
      
      fetch(fileUrl)
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => {
          const type = "text/csv";
          const file = new Blob([arrayBuffer], {
            type,
          });
          // Using the worker to process the file
          fileWorker.postMessage(file);

          // Handle worker messages (parsed data or error)
          fileWorker.onmessage = (event) => {
            const { rows, error } = event.data;

            if (error) {
              setError(error);
              setHasMore(false);
            } else if (Array.isArray(rows)) {
              setData(rows);
              setVisibleRows(rows.slice(0, rowLimit));
            } else {
              setError("Unexpected data format.");
              setHasMore(false);
            }

            setIsLoading(false);
          };

          fileWorker.onerror = (e) => {
            setError("Error reading the file.");
            setIsLoading(false);
          };
        })
        .catch((err) => {
          setError('Error fetching the file.');
          console.error(err);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [fileUrl]);

  // Function to load more rows when scrolling
  const fetchMoreRows = () => {
    const nextRows = data.slice(visibleRows.length, visibleRows.length + rowLimit);
    setVisibleRows((prevRows) => [...prevRows, ...nextRows]);

    if (visibleRows.length + nextRows.length >= data.length) {
      setHasMore(false); // No more rows to load
    }
  };

  const textRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  // Function to check if text is truncated
  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis(); // Check on mount
    window.addEventListener("resize", checkEllipsis); // Recheck on resize

    return () => window.removeEventListener("resize", checkEllipsis); // Cleanup on unmount
  }, [checkEllipsis, fileData?.filename]);

  return (
    <>
      <div className="ExcelHeader">
      <Tooltip title={isEllipsisActive ? fileData?.filename : ""}>
      <div ref={textRef} className="fileName">
        {fileData?.filename}
      </div>
    </Tooltip>        { (type === 'chat' || type === 'system_messages') ? <></> : (
          <div className="fileSize">
            Size: {fileData?.filesize ? formatFileSize(fileData?.filesize) : '0 Mb'}
          </div>
        )}
      </div>
      <InfiniteScroll
        dataLength={visibleRows.length}
        next={fetchMoreRows}
        hasMore={hasMore}
        loader={<></>}
        className="ExcelContainer"
        height={"calc(100vh - 200px)"}
        style={{ width: "100%" }}
      >
        <div className='ExcelInnerContainer'>
          {isLoading ? <Loader /> : 
          <div className="content">
            {error ? (
              <div className="errorContainer">
                <p className="errorMessage">{error}</p>
              </div>
            ) : (
              <div className="spreadSheetContainer">
                <Spreadsheet
                  data={visibleRows.map(row => row.map(cell => ({ value: cell, readOnly: true })))}
                />
              </div>
            )}
          </div>}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default ExcelViewer;
